import withDefaultProps from '@anm/HOCs/withDefaultProps';
import noop from '@anm/helpers/noop';
import React, { FC } from 'react';

import VideoWrapper from './Wrapper';
import usePlyrEvents from './hooks/usePlyrEvents';
import useVideoPlayer from './hooks/useVideoPlayer';

export { VideoWrapper };

export type Player = Plyr;

export type VideoPlayerOptions = {
  autoPlay?: boolean;
  preload?: 'auto' | 'metadata' | 'none';
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
};

export type VideoPlayerProps = VideoEvents & {
  src: string;
  poster?: string;
  options?: VideoPlayerOptions;
  plyrOptions?: Plyr.Options;
  className?: string;
  isCustomStyle?: boolean;
} & DefaultProps;

export type VideoEvents = {
  onTimeChange?: (time: number) => void;
  onMute?: (isMuted: boolean) => void;
  onPause?: () => void;
  onSpeedChange?: (speed: number) => void;
  onVideoPlay?: () => void;
  onVideoLoaded?: () => void;
  onVideoLoadStart?: () => void;
};

export type DefaultProps = Readonly<typeof defaultProps> & {
  playerRef: (player: Player) => void;
};

const defaultProps = {
  videoRef: React.createRef<HTMLVideoElement>(),
  playerRef: noop
};

const CustomWrapper: FC<{ className?: string }> = ({ children, className }) => (
  <div className={className}>{children}</div>
);

const VideoPlayer: FC<VideoPlayerProps> = ({
  onVideoPlay,
  onVideoLoadStart,
  onVideoLoaded,
  videoRef,
  playerRef,
  src,
  poster,
  options,
  onMute,
  onPause,
  onTimeChange,
  onSpeedChange,
  plyrOptions = {},
  isCustomStyle,
  className
}) => {
  const player = useVideoPlayer({
    videoRef,
    playerRef,
    plyrOptions
  });
  const addEvent = usePlyrEvents(player);

  const Wrapper = isCustomStyle ? CustomWrapper : VideoWrapper;

  onTimeChange &&
    addEvent('timeupdate', event => event?.detail.plyr.currentTime && onTimeChange(event.detail.plyr.currentTime));
  onPause && addEvent('pause', onPause);
  onSpeedChange && addEvent('ratechange', event => event?.detail.plyr.speed && onSpeedChange(event.detail.plyr.speed));
  onVideoPlay && addEvent('play', onVideoPlay);
  onMute && addEvent('volumechange', event => onMute(!!event?.detail.plyr.muted));

  return (
    <Wrapper className={className}>
      <video
        id="player"
        src={src}
        ref={videoRef}
        width="100%"
        height="100%"
        poster={poster}
        onLoadStart={onVideoLoadStart}
        onLoadedData={onVideoLoaded}
        {...options}
      />
    </Wrapper>
  );
};

export default withDefaultProps<VideoPlayerProps, typeof defaultProps>(defaultProps)(VideoPlayer);
